'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useLoginState } from '@/hooks/use-login-state'
import {
  PUSH_DISMISS_STORAGE_KEY,
  PUSH_SUBSCRIBE_FAILED_STORAGE_KEY,
  isPushSupported,
  syncPushSubscription,
} from '@/lib/push-notifications-client'

type PermissionState = 'default' | 'denied' | 'granted' | 'unsupported'

export function PushSubscriptionPrompt() {
  const { isLoggedIn, isLoading } = useLoginState()
  const publicKey = process.env.NEXT_PUBLIC_WEB_PUSH_PUBLIC_KEY
  const supported = useMemo(() => isPushSupported(), [])
  const [permission, setPermission] = useState<PermissionState>('default')
  const [dismissed, setDismissed] = useState(false)
  const [subscribeFailed, setSubscribeFailed] = useState(false)
  const [busy, setBusy] = useState(false)
  const [open, setOpen] = useState(false)
  const enableInFlightRef = useRef(false)

  useEffect(() => {
    if (!supported) {
      setPermission('unsupported')
      return
    }
    setPermission(Notification.permission as PermissionState)
    try {
      setDismissed(Boolean(window.localStorage.getItem(PUSH_DISMISS_STORAGE_KEY)))
      setSubscribeFailed(Boolean(window.localStorage.getItem(PUSH_SUBSCRIBE_FAILED_STORAGE_KEY)))
    } catch {
      // Treat localStorage failures as "not dismissed" / not failed.
    }
  }, [supported])

  // If the user has already granted permission, silently make sure the
  // server-side subscription is in sync (e.g. on a new device or after the
  // subscription was rotated by the browser).
  useEffect(() => {
    if (
      !isLoggedIn ||
      !supported ||
      !publicKey ||
      permission !== 'granted' ||
      subscribeFailed ||
      busy
    ) {
      return
    }

    let cancelled = false

    const debug = (step: string, extra?: unknown) => {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.log(`[push-sync] ${step}`, extra ?? '')
      }
    }

    const sync = async () => {
      try {
        if (!cancelled) {
          await syncPushSubscription({ debug, publicKey })
        }
      } catch (error) {
        try {
          window.localStorage.setItem(PUSH_SUBSCRIBE_FAILED_STORAGE_KEY, new Date().toISOString())
          setSubscribeFailed(true)
        } catch {
          // Non-fatal.
        }
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.warn('[push-sync] failed', error)
        }
      }
    }

    void sync()

    return () => {
      cancelled = true
    }
  }, [isLoggedIn, supported, publicKey, permission, subscribeFailed, busy])

  const enable = useCallback(async () => {
    if (!supported || !publicKey || busy || enableInFlightRef.current) return
    enableInFlightRef.current = true

    const debug = (step: string, extra?: unknown) => {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.log(`[push-prompt] ${step}`, extra ?? '')
      }
    }

    setBusy(true)
    try {
      const result = await Notification.requestPermission()
      debug('permission result', result)
      setPermission(result as PermissionState)

      if (result !== 'granted') {
        if (result === 'denied') {
          toast.error('Notifications are blocked. You can re-enable them in your browser settings.')
        } else {
          toast.warning('Permission was not granted.')
        }
        return
      }

      const ok = await syncPushSubscription({ debug, publicKey })

      if (ok) {
        try {
          window.localStorage.removeItem(PUSH_SUBSCRIBE_FAILED_STORAGE_KEY)
          setSubscribeFailed(false)
        } catch {
          // Non-fatal.
        }
        toast.success('Push notifications enabled.')
      } else {
        toast.error('We saved your permission but could not register this device. Try again later.')
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      try {
        window.localStorage.setItem(PUSH_SUBSCRIBE_FAILED_STORAGE_KEY, new Date().toISOString())
        setSubscribeFailed(true)
      } catch {
        // Non-fatal.
      }
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn('[push-prompt] enable failed', error)
      }
      toast.error(`Unable to enable push notifications: ${message}`)
    } finally {
      enableInFlightRef.current = false
      setBusy(false)
    }
  }, [busy, publicKey, supported])

  const dismiss = useCallback(() => {
    setDismissed(true)
    setOpen(false)
    try {
      window.localStorage.setItem(PUSH_DISMISS_STORAGE_KEY, new Date().toISOString())
    } catch {
      // Non-fatal.
    }
  }, [])

  const hiddenReason = isLoading
    ? null
    : !isLoggedIn
      ? 'user not logged in'
      : !supported
        ? 'browser lacks ServiceWorker / PushManager / Notification'
        : !publicKey
          ? 'NEXT_PUBLIC_WEB_PUSH_PUBLIC_KEY is not set (restart `npm run dev` after adding it)'
          : subscribeFailed
            ? `browser push subscription failed earlier (clear localStorage key "${PUSH_SUBSCRIBE_FAILED_STORAGE_KEY}" to retry)`
            : permission !== 'default'
              ? `Notification.permission is "${permission}"`
              : dismissed
                ? `prompt was dismissed earlier (clear localStorage key "${PUSH_DISMISS_STORAGE_KEY}")`
                : null

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production' && hiddenReason) {
      // eslint-disable-next-line no-console
      console.log(`[push-prompt] hidden: ${hiddenReason}`)
    }
  }, [hiddenReason])

  useEffect(() => {
    if (!isLoading && !hiddenReason) {
      setOpen(true)
    }
  }, [hiddenReason, isLoading])

  if (isLoading || hiddenReason) return null

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (busy) return
        if (!nextOpen) {
          dismiss()
          return
        }
        setOpen(true)
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Get Push Notifications</DialogTitle>
          <DialogDescription>
            Stay in the loop with timely updates from HeadCount on this device.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <p className="text-sm text-muted-foreground">
            We will only send important app updates, and you can turn notifications off in
            your browser settings at any time.
          </p>
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" disabled={busy} onClick={dismiss}>
            Not now
          </Button>
          <Button type="button" disabled={busy} onClick={enable}>
            {busy ? 'Enabling...' : 'Enable notifications'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
